/**
 * Playtest & observation: spectator seat, session replay, the playtest dashboard, post-session feedback,
 * the review notice, and the per-campaign DM latency switches (A/B-able).
 */
export const DM_BRAINS = [
    { id: "fast", label: "Fast", help: "Quickest replies: answers live with the Background model, least clever." },
    { id: "balanced", label: "Balanced", help: "The DM model thinks a little before each reply (about 2-5 s). Recommended." },
    { id: "deep", label: "Deep", help: "Thinks hard before every reply. Smartest, but replies can take 15-50 s." },
];
/** xAI models a campaign can pick (exact API ids, checked against xAI's /v1/language-models, Oct 28 2026). */
export const DM_MODELS = [
    { id: "grok-4.7", label: "Grok 4.7", help: "The default: best storytelling and rules judgement. $2 in / $6 out per 1M tokens.", reasoning: true },
    { id: "grok-4.3", label: "Grok 4.3", help: "Cheaper reasoning model. $1.25 in / $2.50 out per 1M tokens.", reasoning: true },
    { id: "grok-4.20-0309-reasoning", label: "Grok 4.20 (reasoning)", help: "Cheaper reasoning model. $1.25 in / $2.50 out per 1M tokens.", reasoning: true },
    { id: "grok-4.20-0309-non-reasoning", label: "Grok 4.20 non-reasoning", help: "Fastest and cheapest; no thinking before it answers. $1.25 in / $2.50 out per 1M tokens.", reasoning: false },
];
export const DEFAULT_DM_MODEL = "grok-4.7";
export const DEFAULT_BG_MODEL = "grok-4.20-0309-non-reasoning";
export const isDmModel = (id) => typeof id === "string" && DM_MODELS.some((m) => m.id === id);
/** reasoning_effort is accepted by grok-4.3 and grok-4.5+; both grok-4.20 models reject it (HTTP 400; checked Oct 28 2026). */
export const supportsLowEffort = (model) => /^grok-4\.(3|[5-9])(?![0-9])/.test(model);
export const DEFAULT_LATENCY = { dmBrain: "balanced", dmModel: DEFAULT_DM_MODEL, bgModel: DEFAULT_BG_MODEL, backgroundThinking: true, streaming: true, earlyContext: true, diceDrafts: true, choiceDrafts: true, combatLookahead: true, filler: true };
export const LATENCY_LABELS = {
    backgroundThinking: { label: "Background thinking", help: "After each reply the Background model quietly plans ahead (threads, NPC motives, who's been quiet). Never spoken. Small extra AI cost." },
    streaming: { label: "Stream replies", help: "Speak sentence by sentence as the reply is written (biggest speed-up)." },
    earlyContext: { label: "Early start", help: "Load the game state and memory while the player is still talking." },
    diceDrafts: { label: "Dice pre-drafts", help: "Draft the success and failure outcomes while a roll is pending. Small extra AI cost." },
    choiceDrafts: { label: "Choice pre-drafts", help: "When the DM offers 2-3 options, draft what happens for each while the party decides. Small extra AI cost." },
    combatLookahead: { label: "Combat lookahead", help: "Pre-plan the next monster's move during a player's turn. Small extra AI cost." },
    filler: { label: "Quick acknowledgement", help: "If the reply takes more than ~1.5 s, the DM says a short in-character line first." },
};
export function normalizeLatency(v) {
    const o = v && typeof v === "object" ? v : {};
    const out = { ...DEFAULT_LATENCY };
    for (const k of Object.keys(LATENCY_LABELS))
        if (typeof o[k] === "boolean")
            out[k] = o[k];
    if (o.dmBrain === "fast" || o.dmBrain === "balanced" || o.dmBrain === "deep")
        out.dmBrain = o.dmBrain;
    if (isDmModel(o.dmModel))
        out.dmModel = o.dmModel;
    if (isDmModel(o.bgModel))
        out.bgModel = o.bgModel;
    return out;
}
export const REVIEW_NOTICE = "Sessions in this campaign are reviewed by the Host (and the Admin) to improve the game: transcripts, rolls, DM response times " +
    "and your optional ratings.";
// ---------------------------------------------------------------- heuristics (pure; shared by server and tests)
export function percentile(values, p) {
    if (!values.length)
        return null;
    const s = [...values].sort((a, b) => a - b);
    const i = Math.min(s.length - 1, Math.max(0, Math.ceil((p / 100) * s.length) - 1));
    return s[i];
}
export const average = (values) => values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
const words = (t) => t.toLowerCase().replace(/[^\p{L}\p{N}' ]+/gu, " ").split(/\s+/).filter((w) => w.length > 1);
/** Two utterances look like a repeat/rephrase: mostly the same words (Jaccard ≥ 0.6) or one contains the other. */
export function nearDuplicate(a, b) {
    const wa = words(a);
    const wb = words(b);
    if (wa.length < 2 || wb.length < 2)
        return false;
    const A = new Set(wa);
    const B = new Set(wb);
    let inter = 0;
    for (const w of A)
        if (B.has(w))
            inter++;
    const jaccard = inter / (A.size + B.size - inter);
    if (jaccard >= 0.6)
        return true;
    const sa = wa.join(" ");
    const sb = wb.join(" ");
    return (sa.length >= 12 && sb.includes(sa)) || (sb.length >= 12 && sa.includes(sb));
}
/** Repeats/rephrases: the same player says nearly the same thing within `windowMs` (STT misses, the DM didn't hear). */
export function countRepeats(lines, windowMs = 60_000) {
    let n = 0;
    const players = lines.filter((l) => !l.isDm && l.source !== "system");
    for (let i = 1; i < players.length; i++) {
        const cur = players[i];
        for (let j = i - 1; j >= 0; j--) {
            const prev = players[j];
            if (cur.at - prev.at > windowMs)
                break;
            if (prev.speakerName === cur.speakerName && nearDuplicate(prev.text, cur.text)) {
                n++;
                break;
            }
        }
    }
    return n;
}
/** Silences longer than `thresholdSec` between consecutive lines (anyone). */
export function idleGaps(lines, thresholdSec) {
    const out = [];
    for (let i = 1; i < lines.length; i++) {
        const gap = (lines[i].at - lines[i - 1].at) / 1000;
        if (gap >= thresholdSec)
            out.push({ at: new Date(lines[i - 1].at).toISOString(), seconds: Math.round(gap) });
    }
    return out;
}
/** Talk balance per player (DM and system lines excluded). */
export function talkBalance(lines) {
    const by = new Map();
    for (const l of lines) {
        if (l.isDm || l.source === "system")
            continue;
        const key = l.speakerId ?? `name:${l.speakerName}`;
        const t = by.get(key) ?? { userId: l.speakerId, name: l.speakerName, utterances: 0, words: 0, share: 0 };
        t.utterances++;
        t.words += words(l.text).length;
        by.set(key, t);
    }
    const all = [...by.values()];
    const total = all.reduce((a, t) => a + t.words, 0);
    for (const t of all)
        t.share = total ? t.words / total : 0;
    return all.sort((a, b) => b.words - a.words);
}
// ---------------------------------------------------------------- the DM's quick acknowledgements
/** Short in-character lines the DM can say while the real reply is still coming. */
export const FILLER_LINES = [
    "Hmm, let me see...",
    "Ah, interesting...",
    "Very well...",
    "One moment, adventurer...",
    "Let's find out...",
    "Hmm...",
    "Mm, I see...",
    "Right then...",
    "Ooh, bold...",
    "Let me consult the fates...",
    "Alright...",
    "Hold that thought...",
];
/** A filler that wasn't used in the last few picks (so it doesn't sound repetitive). */
export function pickFiller(recent, rand = Math.random) {
    const avoid = new Set(recent.slice(-5));
    const choices = FILLER_LINES.map((_, i) => i).filter((i) => !avoid.has(i));
    return choices[Math.floor(rand() * choices.length)] ?? 0;
}
/** Used when there isn't enough recent usage to measure: typical DM turns are mostly prompt, some of it cached. */
export const DEFAULT_TOKEN_MIX = { uncached: 0.55, cached: 0.35, output: 0.1 };
/**
 * Blended price per 1M tokens for each pickable model (mix-weighted input, cached input and output prices), and how
 * many times the cheapest priced model it costs, rounded to 1 decimal. Missing cached price = input price.
 */
export function relativeModelCosts(prices, mix) {
    const p = (model, kind) => prices.find((x) => x.model === model && x.kind === kind)?.usdPerMillion ?? null;
    const blended = DM_MODELS.map((m) => {
        const inp = p(m.id, "input"), out = p(m.id, "output");
        const cached = p(m.id, "cached_input") ?? inp;
        if (inp === null || out === null || cached === null)
            return { id: m.id, blendedUsdPerMillion: null };
        return { id: m.id, blendedUsdPerMillion: Math.round((mix.uncached * inp + mix.cached * cached + mix.output * out) * 10000) / 10000 };
    });
    const min = Math.min(...blended.map((b) => b.blendedUsdPerMillion ?? Infinity));
    return blended.map((b) => ({ ...b, relative: b.blendedUsdPerMillion === null || !Number.isFinite(min) || min <= 0 ? null : Math.round((b.blendedUsdPerMillion / min) * 10) / 10 }));
}
/** Normalise measured token counts into a mix; too little data falls back to the default. */
export function tokenMix(uncached, cached, output, minTokens = 100_000) {
    const total = uncached + cached + output;
    if (!(total >= minTokens))
        return { mix: DEFAULT_TOKEN_MIX, source: "default" };
    return { mix: { uncached: uncached / total, cached: cached / total, output: output / total }, source: "usage" };
}
//# sourceMappingURL=playtest.js.map